import { useMemo } from "react";
import { Preview } from "./preview";
import { Special } from "./special";
import { DivContentsBox } from "./style";
import { Episode } from "~/components/blocks/product/common/blocks/product-tab-contents/episode";
import { ProductInfo } from "~/components/blocks/product/common/blocks/product-tab-contents/product-info";
import { Purchased } from "~/components/blocks/product/common/blocks/product-tab-contents/purchased";
import { Review } from "~/components/blocks/product/common/blocks/product-tab-contents/review";
import { MULTI_SELECT_TABS } from "~/components/blocks/product/const";
import { isVideoSeason } from "~/components/blocks/product/type-guard";
import { useQueryString } from "~/hooks/use-query-string";
import type { G_GetVideoQuery } from "~/lib/graphql";
import { G_VideoSeasonType } from "~/lib/graphql";
import { useAuth } from "~/provider/auth";

export type VideoSeasonTabContentsProps = {
  /** videoクエリを叩いて取得したデータ */
  videoData: G_GetVideoQuery["video"];
  /** 現在選択中のタブの値 */
  selectedTabValue: string;
  /** エピソードの合計数 */
  totalEpisodeLength: number;
  /** 購入済みの合計数 */
  totalPurchasedLength: number;
  /** 特典映像の合計数 */
  totalSpecialLength: number;
  /** 予告編映像の合計数 */
  totalPreviewLength: number;
};

/**
 * 複数話 | 作品末端のタブに紐づくコンテンツを表示するコンポーネント
 * - SEO的な都合上、Tab切り替えでコンテンツのマウント/アンマウントをするのは良くない
 * - なので、全てレンダリングした状態で表示/非表示を指定する
 * @param props {@link VideoSeasonTabContentsProps}
 */
export const VideoSeasonTabContents = (props: VideoSeasonTabContentsProps) => {
  const {
    videoData,
    selectedTabValue,
    totalEpisodeLength,
    totalPurchasedLength,
    totalSpecialLength,
    totalPreviewLength,
  } = props;

  const { content } = useQueryString();
  const { isLoggedIn } = useAuth();

  /** エピソード末端を開く場合に使用。`after`に指定するための数値計算に使用する */
  const episodeNumber = useMemo(() => {
    // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
    return (isVideoSeason(videoData) && content !== undefined && videoData.episode?.episodeNumber) || 1;
  }, [content, videoData]);

  return (
    <div>
      {videoData.seasonType === G_VideoSeasonType.MultiEpisodes && (
        <DivContentsBox isShow={selectedTabValue === MULTI_SELECT_TABS.EPISODE}>
          <Episode
            episodeNumber={episodeNumber}
            seasonType={videoData.seasonType}
            totalEpisodeLength={totalEpisodeLength}
          />
        </DivContentsBox>
      )}

      {isLoggedIn && videoData.seasonType === G_VideoSeasonType.MultiEpisodes && (
        <DivContentsBox isShow={selectedTabValue === MULTI_SELECT_TABS.PURCHASED}>
          <Purchased seasonType={videoData.seasonType} totalPurchasedLength={totalPurchasedLength} />
        </DivContentsBox>
      )}

      <DivContentsBox isShow={selectedTabValue === MULTI_SELECT_TABS.SPECIAL}>
        <Special totalSpecialLength={totalSpecialLength} />
      </DivContentsBox>

      <DivContentsBox isShow={selectedTabValue === MULTI_SELECT_TABS.PRODUCT_INFO}>
        <ProductInfo videoData={videoData} />
      </DivContentsBox>

      <DivContentsBox isShow={selectedTabValue === MULTI_SELECT_TABS.PREVIEW}>
        <Preview totalPreviewLength={totalPreviewLength} />
      </DivContentsBox>

      <DivContentsBox isShow={selectedTabValue === MULTI_SELECT_TABS.REVIEW}>
        <Review videoData={videoData} />
      </DivContentsBox>
    </div>
  );
};
